import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Navbar.scss";
import {
    IoNotificationsOutline,
    IoChevronDown,
    IoLogOut,
    IoPersonOutline,
    IoMenuSharp,
    IoCheckmarkCircleOutline,
    IoClose,
    IoSearchOutline,
} from "react-icons/io5";
import InputSearch from "../../common/Inputs/InputSearch/InputSearch";
import InputDropdown from "../../common/Inputs/InputDropdown/InputDropdown";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { tiempoDesde } from "../../../utils/utils";
import { SearchModal } from "../../common/Search/SearchModal";
import { useSearch } from "../../../context/SearchContext";
import { PiFactory } from "react-icons/pi";
import { IoConstructOutline } from "react-icons/io5";
import { LuUsers } from "react-icons/lu";
import { useTranslation } from "react-i18next";

const typeIconMap = {
    centroProductor: () => <PiFactory />,
    gestores: () => <LuUsers />,
    obrasMenores: () => <IoConstructOutline />,
};

const typeFieldsMap = {
    centroProductor: ["codObra", "nombre", "location"],
    gestores: ["codProveedor", "nombre", "contact"],
    obrasMenores: ["codObraMenor", "nombre", "status"],
};

const typeRouteBaseMap = {
    centroProductor: "/productores",
    gestores: "/gestores",
    obrasMenores: "/obras-menores",
};

const Navbar = ({
    onToggleSidebar,
    isSidebarOpen,
    isSidebarCollapsed,
    user,
    onLogout,
    userImage,
}) => {
    const { t } = useTranslation();
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [notificaciones, setNotificaciones] = useState([]);
    const [error, setError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const { selectedZone, setSelectedZone, zoneOptions } = useZone();
    const navigate = useNavigate();
    const [isOpenSearch, setIsOpenSearch] = useState(false);
    const { searchElements, loading, errorFetch, fetchAllData } = useSearch();

    const profileRef = useRef(null);
    const notificationRef = useRef(null);

    // Generar iniciales del usuario
    const getUserInitials = () => {
        if (!user || !user.name) return "U";

        const names = user.name.split(" ");
        if (names.length >= 2) {
            return (names[0][0] + names[1][0]).toUpperCase();
        }
        return user.name[0].toUpperCase();
    };

    useEffect(() => {
        fetchAllData();
    }, [selectedZone, fetchAllData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                profileRef.current &&
                !profileRef.current.contains(event.target)
            ) {
                setShowProfileDropdown(false);
            }
            if (
                notificationRef.current &&
                !notificationRef.current.contains(event.target)
            ) {
                setShowNotifications(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const notifications = [
        {
            id: 1,
            message: "Nuevo informe disponible",
            time: "Hace 5 min",
            read: false,
        },
        {
            id: 2,
            message: "Actualización del sistema",
            time: "Hace 1 hora",
            read: false,
        },
        {
            id: 3,
            message: "Reunión programada",
            time: "Hace 2 horas",
            read: true,
        },
    ];

    const fetchNotificaciones = async () => {
        const api = getApiInstance();
        try {
            let notificacionesData = [];

            const response = await api.get(
                `/api/Notificacion/GetNotificacionesNoReadByUser`
            );
            notificacionesData = response.data;

            setUnreadCount(notificacionesData.length);

            setNotificaciones(notificacionesData);
            setError(null);
        } catch (err) {
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        fetchNotificaciones();
    }, []);

    const markAsRead = async (codNotificacion) => {
        const api = getApiInstance();
        try {
            await api.put(`/api/Notificacion/MarkRead/${codNotificacion}`);
            setNotificaciones((prevNotificaciones) =>
                prevNotificaciones.filter(
                    (n) => n.codNotificacion !== codNotificacion
                )
            );
            setUnreadCount((prevCount) => prevCount - 1);
        } catch (err) {
            console.error("Error al marcar la notificación como leída:", err);
        }
    };

    const handleZoneChange = (value) => {
        setSelectedZone(value);
        console.log("Zona seleccionada:", value);
    };

    const handleLogout = () => {
        setShowProfileDropdown(false);
        onLogout();
        navigate("/login");
    };

    const handleProfileClick = () => {
        setShowProfileDropdown(false);
        navigate("/perfil-usuario");
    };

    return (
        <nav
            className={`navbar ${
                isSidebarCollapsed ? "navbar--collapsed" : ""
            }`}
        >
            <button
                className="navbar__mobile-menu"
                onClick={onToggleSidebar}
                aria-label="Toggle menu"
            >
                {isSidebarOpen ? <IoClose /> : <IoMenuSharp />}
            </button>

            <div
                className={`navbar__search ${
                    isMobileSearchOpen ? "navbar__search--mobile-open" : ""
                }`}
            >

            <div
            className="search-bar__container"
             onClick={() => {
        setIsOpenSearch(true);
      }}
            >
                <SearchModal placeholder={t('search.placeholder')} searchElements={searchElements} typeIconMap={typeIconMap}
                typeFieldsMap={typeFieldsMap} typeRouteBaseMap={typeRouteBaseMap}
                />
            </div>
            </div>

            <div className="navbar__actions">
                <button
                    className="navbar__mobile-search-btn"
                    onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                    aria-label="Toggle search"
                >
                    <IoSearchOutline />
                </button>

                <div className="navbar__notification" ref={notificationRef}>
                    <button
                        className="navbar__notification-btn"
                        onClick={() => setShowNotifications(!showNotifications)}
                        aria-label="Notifications"
                    >
                        <IoNotificationsOutline className="navbar__notification-icon" />
                        {unreadCount > 0 && (
                            <span className="navbar__notification-badge">
                                {unreadCount}
                            </span>
                        )}
                    </button>

                    {showNotifications && (
                        <div className="navbar__notification-dropdown">
                            <div className="navbar__notification-header">
                                <h3>Notificaciones</h3>
                                <span className="navbar__notification-count">
                                    {unreadCount} nuevas
                                </span>
                            </div>
                            <div className="navbar__notification-list">
                                {notificaciones.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`navbar__notification-item ${
                                            !notification.usuarios[0].read
                                                ? "navbar__notification-item--unread"
                                                : ""
                                        }`}
                                    >
                                        <div className="navbar__notification-content">
                                            <p className="navbar__notification-message">
                                                {notification.descr}
                                            </p>
                                            <span className="navbar__notification-time">
                                                {tiempoDesde(
                                                    notification.fecha
                                                )}
                                            </span>
                                        </div>
                                        {!notification.usuarios[0].read && (
                                            <div className="navbar__notification-dot"></div>
                                        )}

                                        <div className="navbar__notification-actions">
                                            <button
                                                className="navbar__notification-btn"
                                                onClick={() =>
                                                    markAsRead(
                                                        notification.codNotificacion
                                                    )
                                                }
                                                title="Marcar como leída"
                                            >
                                                <IoCheckmarkCircleOutline />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* <div className="navbar__zone">
                    <InputDropdown
                        options={zoneOptions}
                        value={selectedZone}
                        onChange={handleZoneChange}
                        className="navbar__zone-dropdown"
                    />
                </div> */}

                {/* Perfil de usuario */}
                <div className="navbar__profile" ref={profileRef}>
                    <button
                        className="navbar__profile-btn"
                        onClick={() =>
                            setShowProfileDropdown(!showProfileDropdown)
                        }
                        aria-label="User menu"
                    >
                        <div className="navbar__profile-avatar">
                            {userImage ? (
                                <img
                                    src={userImage}
                                    alt="Usuario"
                                    className="navbar__profile-img"
                                />
                            ) : (
                                <span className="navbar__profile-initials">
                                    {getUserInitials()}
                                </span>
                            )}
                        </div>
                        <IoChevronDown className="navbar__profile-arrow" />
                    </button>

                    {showProfileDropdown && (
                        <div className="navbar__profile-dropdown">
                            <div className="navbar__profile-dropdown-header">
                                <div className="navbar__profile-dropdown-avatar">
                                    <span className="navbar__profile-dropdown-initials">
                                        {getUserInitials()}
                                    </span>
                                </div>
                                <div className="navbar__profile-dropdown-info">
                                    <span className="navbar__profile-dropdown-name">
                                        {user?.name || "Usuario"}
                                    </span>
                                    <span className="navbar__profile-dropdown-email">
                                        {user?.email || "usuario@empresa.com"}
                                    </span>
                                </div>
                            </div>
                            <div className="navbar__profile-dropdown-menu">
                                <button
                                    onClick={handleProfileClick}
                                    className="navbar__profile-dropdown-item"
                                >
                                    <IoPersonOutline />
                                    <span>Mi Perfil</span>
                                </button>
                                <div className="navbar__profile-dropdown-divider"></div>
                                <button
                                    onClick={handleLogout}
                                    className="navbar__profile-dropdown-item navbar__profile-dropdown-logout"
                                >
                                    <IoLogOut />
                                    <span>Cerrar Sesión</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
